import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePagBank } from '../hooks/usePagBank';
import { buildApiUrl } from '../services/api';
import {
  type BillingOfferCode,
  type PaymentFormData,
  createEmptyPaymentFormData
} from '../types/payment';
import {
  BILLING_OFFERS,
  DEFAULT_BILLING_OFFER_CODE,
  formatCurrency,
  getBillingOffer
} from '../utils/billingOffers';
import { maskCPF } from '../utils/documentUtils';
import { getBrandName } from '../utils/branding';

type CurrentSubscription = {
  id: string;
  billingOfferCode?: BillingOfferCode | null;
  planName?: string | null;
  paymentMode?: string | null;
  billingCycle?: string | null;
  recurring?: boolean;
  amountCents?: number | null;
  providerStatus?: string | null;
  currentPeriodEnd?: string | null;
  nextChargeAt?: string | null;
  canceledAt?: string | null;
};

const isValidCPF = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const numbers = digits.split('').map(Number);
  const firstSum = numbers.slice(0, 9).reduce((sum, num, index) => sum + num * (10 - index), 0);
  let firstCheck = (firstSum * 10) % 11;
  if (firstCheck === 10) firstCheck = 0;
  if (firstCheck !== numbers[9]) return false;

  const secondSum = numbers.slice(0, 10).reduce((sum, num, index) => sum + num * (11 - index), 0);
  let secondCheck = (secondSum * 10) % 11;
  if (secondCheck === 10) secondCheck = 0;

  return secondCheck === numbers[10];
};

const formatCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 19);
  return digits.replace(/(.{4})/g, '$1 ').trim();
};

const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('pt-BR');
};

const whatsappSupportUrl = 'https://wa.me/5561993584522?text=Estou%20tendo%20problemas%20com%20cadastro%20do%20AuxiliaJud%20e%20preciso%20de%20ajuda.';

function WhatsAppIcon() {
  return (
    <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-8.78 14.79L2 22l5.34-1.18A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4.07-1.11l-.29-.17-3.17.7.71-3.09-.19-.31A8 8 0 1 1 12 20Zm4.39-5.46c-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1-.37-1.91-1.18-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.79-.2-.47-.41-.41-.56-.41h-.48c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.52.58.18 1.11.16 1.53.1.47-.07 1.43-.58 1.63-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z" />
    </svg>
  );
}

export function PaymentPage() {
  const navigate = useNavigate();
  const { paymentNotice, paymentRequired, checkAuth, logout } = useAuth();
  const { encryptCard } = usePagBank();
  const brandName = getBrandName();

  const [paymentData, setPaymentData] = useState<PaymentFormData>(createEmptyPaymentFormData);
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});
  const [selectedBillingOfferCode, setSelectedBillingOfferCode] = useState<BillingOfferCode>(
    (paymentNotice?.billingOfferCode as BillingOfferCode) || DEFAULT_BILLING_OFFER_CODE
  );
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEncryptingCard, setIsEncryptingCard] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [lastCepLookup, setLastCepLookup] = useState('');
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);

  const selectedBillingOffer = useMemo(
    () => getBillingOffer(selectedBillingOfferCode),
    [selectedBillingOfferCode]
  );

  const trialEndsAtLabel = useMemo(
    () => formatDate(paymentNotice?.trialEndsAt ?? paymentNotice?.accessEndsAt ?? paymentNotice?.dueAt),
    [paymentNotice?.trialEndsAt, paymentNotice?.accessEndsAt, paymentNotice?.dueAt]
  );

  const activeSubscriptionOffer = useMemo(() => {
    if (!currentSubscription?.billingOfferCode) return null;
    return getBillingOffer(currentSubscription.billingOfferCode);
  }, [currentSubscription?.billingOfferCode]);

  const loadCurrentSubscription = async () => {
    try {
      setIsLoadingSubscription(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/payments/subscription'), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.message || 'Não foi possível carregar a assinatura atual');
      }

      setCurrentSubscription(result?.data?.subscription || null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar assinatura';
      toast.error(message);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  useEffect(() => {
    void loadCurrentSubscription();
  }, []);

  useEffect(() => {
    if (paymentNotice?.billingOfferCode) {
      setSelectedBillingOfferCode(paymentNotice.billingOfferCode);
      return;
    }

    if (currentSubscription?.billingOfferCode) {
      setSelectedBillingOfferCode(currentSubscription.billingOfferCode);
    }
  }, [currentSubscription?.billingOfferCode, paymentNotice?.billingOfferCode]);

  useEffect(() => {
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');
    if (cepDigits.length !== 8 || cepDigits === lastCepLookup) return;

    let isActive = true;
    const timer = setTimeout(async () => {
      setIsCepLoading(true);

      try {
        const response = await fetch(buildApiUrl(`/cep/${cepDigits}`));
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (isActive) {
            setPaymentErrors((prev) => ({ ...prev, billingZip: data?.error || 'CEP não encontrado' }));
            setLastCepLookup(cepDigits);
          }
          return;
        }

        if (!isActive || !data) return;

        setPaymentData((prev) => ({
          ...prev,
          billingStreet: prev.billingStreet || data.street || '',
          billingDistrict: prev.billingDistrict || data.district || '',
          billingCity: prev.billingCity || data.city || '',
          billingState: prev.billingState || data.state || '',
          billingComplement: prev.billingComplement || data.complement || ''
        }));

        setPaymentErrors((prev) => {
          const next = { ...prev };
          delete next.billingZip;
          delete next.billingStreet;
          delete next.billingDistrict;
          delete next.billingCity;
          delete next.billingState;
          return next;
        });
        setLastCepLookup(cepDigits);
      } catch {
        if (isActive) {
          setPaymentErrors((prev) => ({ ...prev, billingZip: 'Erro ao consultar CEP' }));
          setLastCepLookup(cepDigits);
        }
      } finally {
        if (isActive) setIsCepLoading(false);
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [paymentData.billingZip, lastCepLookup]);

  const validatePayment = () => {
    const nextErrors: Record<string, string> = {};
    const cardDigits = paymentData.cardNumber.replace(/\D/g, '');
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');
    const phoneDigits = paymentData.contactPhone.replace(/\D/g, '');

    if (phoneDigits.length < 10) nextErrors.contactPhone = 'Telefone inválido';
    if (!paymentData.holderName.trim()) nextErrors.holderName = 'Nome do titular é obrigatório';
    if (!isValidCPF(paymentData.holderDocument)) nextErrors.holderDocument = 'CPF do titular inválido';
    if (cardDigits.length < 13) nextErrors.cardNumber = 'Número do cartão inválido';
    if (!/^\d{2}\/\d{2}$/.test(paymentData.cardExpiry)) nextErrors.cardExpiry = 'Validade inválida';
    if (!/^\d{3,4}$/.test(paymentData.cardCvv)) nextErrors.cardCvv = 'CVV inválido';
    if (cepDigits.length < 8) nextErrors.billingZip = 'CEP inválido';
    if (!paymentData.billingStreet.trim()) nextErrors.billingStreet = 'Endereço é obrigatório';
    if (!paymentData.billingNumber.trim()) nextErrors.billingNumber = 'Número é obrigatório';
    if (!paymentData.billingDistrict.trim()) nextErrors.billingDistrict = 'Bairro é obrigatório';
    if (!paymentData.billingCity.trim()) nextErrors.billingCity = 'Cidade é obrigatória';
    if (!/^[A-Za-z]{2}$/.test(paymentData.billingState.trim())) nextErrors.billingState = 'UF inválida';

    setPaymentErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitPayment = async () => {
    if (!validatePayment()) return;

    let encryptedCard = '';
    try {
      setIsEncryptingCard(true);
      encryptedCard = await encryptCard(paymentData);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao criptografar cartão';
      toast.error(message);
      return;
    } finally {
      setIsEncryptingCard(false);
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/payments/subscribe'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          billingOfferCode: selectedBillingOffer.code,
          contactPhone: paymentData.contactPhone,
          holderName: paymentData.holderName,
          holderDocument: paymentData.holderDocument,
          billingZip: paymentData.billingZip,
          billingStreet: paymentData.billingStreet,
          billingNumber: paymentData.billingNumber,
          billingComplement: paymentData.billingComplement,
          billingDistrict: paymentData.billingDistrict,
          billingCity: paymentData.billingCity,
          billingState: paymentData.billingState,
          cardCvv: paymentData.cardCvv,
          cardEncrypted: encryptedCard
        })
      });

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.message || result?.error || 'Não foi possível processar a cobrança');
      }

      toast.success(result?.message || 'Cobrança configurada com sucesso');
      setPaymentData(createEmptyPaymentFormData());
      setPaymentErrors({});
      await Promise.all([checkAuth(), loadCurrentSubscription()]);
      navigate('/contatos');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao processar o pagamento';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentSubscription?.recurring) {
      toast.error('Apenas assinaturas recorrentes podem ser canceladas por aqui.');
      return;
    }

    try {
      setIsCancellingSubscription(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/payments/subscription/cancel'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: 'Cancelamento solicitado na plataforma'
        })
      });

      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.message || 'Não foi possível cancelar a assinatura');
      }

      toast.success(result?.message || 'Assinatura cancelada com sucesso');
      await Promise.all([checkAuth(), loadCurrentSubscription()]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao cancelar assinatura';
      toast.error(message);
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const offerPriceLabel = selectedBillingOffer.code === 'MONTHLY_RECURRING'
    ? `R$ ${formatCurrency(selectedBillingOffer.amountCents)}/mês`
    : selectedBillingOffer.code === 'ANNUAL_RECURRING'
      ? `12 x R$ ${formatCurrency(selectedBillingOffer.amountCents)}`
      : `R$ ${formatCurrency(selectedBillingOffer.amountCents)}/ano`;

  const offerSecondaryLabel = selectedBillingOffer.code === 'ANNUAL_UPFRONT'
    ? `De R$ ${formatCurrency(selectedBillingOffer.originalAmountCents)}/ano`
    : selectedBillingOffer.code === 'ANNUAL_RECURRING'
      ? '12x de R$ 89,90, sem cobrar o total de uma vez no cartao'
      : 'Renovacao automatica mensal de R$ 109,90';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-[68%] p-8 lg:p-10 space-y-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-3xl font-bold text-gray-900">Assinatura e cobrança</h1>
              <p className="text-sm text-gray-600">
                {paymentRequired
                  ? 'Seu acesso precisa ser regularizado. Escolha o plano e confirme a cobrança.'
                  : 'Gerencie seu plano, atualize seu cartão e cancele a recorrência quando precisar.'}
              </p>
            </div>

            {(paymentNotice?.message || trialEndsAtLabel !== '-') && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
                {paymentNotice?.message && (
                  <p>
                    <strong>Aviso:</strong> {paymentNotice.message}
                  </p>
                )}
                {trialEndsAtLabel !== '-' && (
                  <p>
                    <strong>Data de referência:</strong> {trialEndsAtLabel}
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-3">
              {Object.values(BILLING_OFFERS).map((offer) => {
                const isSelected = selectedBillingOfferCode === offer.code;
                return (
                  <button
                    key={offer.code}
                    type="button"
                    onClick={() => setSelectedBillingOfferCode(offer.code)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      isSelected
                        ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                        : 'border-gray-200 bg-white text-gray-900 hover:border-slate-400'
                    }`}
                  >
                    <p className="text-sm font-semibold">{offer.label}</p>
                    <p className={`mt-2 text-xl font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                      {offer.code === 'MONTHLY_RECURRING'
                        ? `R$ ${formatCurrency(offer.amountCents)}/mês`
                        : offer.code === 'ANNUAL_RECURRING'
                          ? `12 x R$ ${formatCurrency(offer.amountCents)}`
                        : `R$ ${formatCurrency(offer.amountCents)}/ano`}
                    </p>
                    <p className={`mt-2 text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                      {offer.description}
                    </p>
                    {offer.discountPercent > 0 && (
                      <p className={`mt-3 text-xs font-semibold ${isSelected ? 'text-emerald-200' : 'text-emerald-700'}`}>
                        {offer.discountPercent}% de desconto à vista
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-5">
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Dados do titular</h2>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefone do responsável</label>
                  <input
                    type="text"
                    value={paymentData.contactPhone}
                    onChange={(e) => setPaymentData((prev) => ({ ...prev, contactPhone: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.contactPhone && <p className="mt-2 text-sm text-red-600">{paymentErrors.contactPhone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome impresso no cartão</label>
                  <input
                    type="text"
                    value={paymentData.holderName}
                    onChange={(e) => setPaymentData((prev) => ({ ...prev, holderName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.holderName && <p className="mt-2 text-sm text-red-600">{paymentErrors.holderName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CPF do titular</label>
                  <input
                    type="text"
                    value={paymentData.holderDocument}
                    onChange={(e) => setPaymentData((prev) => ({ ...prev, holderDocument: maskCPF(e.target.value) }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.holderDocument && <p className="mt-2 text-sm text-red-600">{paymentErrors.holderDocument}</p>}
                </div>

                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Cartão</h2>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número do cartão</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    value={paymentData.cardNumber}
                    onChange={(e) => setPaymentData((prev) => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
                    placeholder="0000 0000 0000 0000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.cardNumber && <p className="mt-2 text-sm text-red-600">{paymentErrors.cardNumber}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Validade (MM/AA)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      value={paymentData.cardExpiry}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, cardExpiry: formatExpiry(e.target.value) }))}
                      placeholder="MM/AA"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardExpiry && <p className="mt-2 text-sm text-red-600">{paymentErrors.cardExpiry}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      value={paymentData.cardCvv}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, cardCvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      placeholder="123"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardCvv && <p className="mt-2 text-sm text-red-600">{paymentErrors.cardCvv}</p>}
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Endereço de cobrança</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CEP</label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={paymentData.billingZip}
                        onChange={(e) => setPaymentData((prev) => ({ ...prev, billingZip: formatCep(e.target.value) }))}
                        placeholder="00000-000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                        style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                        disabled={isSubmitting}
                      />
                      {isCepLoading && <div className="absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">Buscando...</div>}
                    </div>
                    {paymentErrors.billingZip && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingZip}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
                    <input
                      type="text"
                      value={paymentData.billingStreet}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingStreet: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingStreet && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingStreet}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                    <input
                      type="text"
                      value={paymentData.billingNumber}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingNumber: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingNumber && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingNumber}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                    <input
                      type="text"
                      value={paymentData.billingComplement}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingComplement: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
                    <input
                      type="text"
                      value={paymentData.billingDistrict}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingDistrict: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingDistrict && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingDistrict}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cidade</label>
                    <input
                      type="text"
                      value={paymentData.billingCity}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingCity: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingCity && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingCity}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">UF</label>
                    <input
                      type="text"
                      value={paymentData.billingState}
                      onChange={(e) => setPaymentData((prev) => ({ ...prev, billingState: e.target.value.toUpperCase().slice(0, 2) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingState && <p className="mt-2 text-sm text-red-600">{paymentErrors.billingState}</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={logout}
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  disabled={isSubmitting || isEncryptingCard}
                >
                  Sair
                </button>
                <a
                  href={whatsappSupportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600"
                >
                  <WhatsAppIcon />
                  Ajuda pelo WhatsApp
                </a>
              </div>
              <button
                type="button"
                onClick={handleSubmitPayment}
                disabled={isSubmitting || isEncryptingCard}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}
              >
                {isEncryptingCard ? 'Criptografando cartão...' : isSubmitting ? 'Processando...' : 'Salvar cobrança'}
              </button>
            </div>
          </div>

          <div className="lg:w-[32%] border-t lg:border-t-0 lg:border-l border-gray-200 bg-gray-50 p-8 lg:p-10 space-y-5">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-2">
              <p className="text-sm text-gray-500">{selectedBillingOffer.planName}</p>
              <p className="text-2xl font-semibold text-gray-900">{offerPriceLabel}</p>
              <p className="text-sm text-gray-500">{offerSecondaryLabel}</p>
              {selectedBillingOffer.discountPercent > 0 && (
                <p className="text-xs text-emerald-700 font-semibold">
                  Economia de {selectedBillingOffer.discountPercent}% no pagamento único anual
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Assinatura atual</h2>
                {isLoadingSubscription && <span className="text-xs text-gray-400">Carregando...</span>}
              </div>

              {currentSubscription ? (
                <>
                  <p className="text-base font-semibold text-gray-900">
                    {activeSubscriptionOffer?.label || currentSubscription.planName || 'Plano configurado'}
                  </p>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>Status: <strong>{currentSubscription.providerStatus || 'Sem status'}</strong></p>
                    <p>Vigência atual até: <strong>{formatDate(currentSubscription.currentPeriodEnd)}</strong></p>
                    <p>Próxima cobrança: <strong>{formatDate(currentSubscription.nextChargeAt)}</strong></p>
                    <p>Modelo: <strong>{currentSubscription.recurring ? 'Recorrente' : 'Pagamento único'}</strong></p>
                  </div>
                  {currentSubscription.recurring && !currentSubscription.canceledAt && (
                    <button
                      type="button"
                      onClick={handleCancelSubscription}
                      disabled={isCancellingSubscription}
                      className="w-full rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-60"
                    >
                      {isCancellingSubscription ? 'Cancelando...' : 'Cancelar renovação automática'}
                    </button>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">Nenhuma assinatura ativa encontrada.</p>
              )}
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <strong>Importante:</strong> a recorrência é criada e o cancelamento é propagado pela plataforma integrada ao PagBank.
            </div>
            <div className="text-xs text-gray-500">
              Ao continuar, você autoriza a cobrança do plano selecionado e a renovação automática quando o plano for recorrente.
            </div>
            <a
              href={whatsappSupportUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-500 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600"
            >
              <WhatsAppIcon />
              Estou com problemas no pagamento
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}


